/* eslint-disable no-restricted-imports */
import {
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  Text,
} from "@chakra-ui/react";
import { FormikErrors, useFormikContext } from "formik";
import React from "react";
import { RiDeleteBin6Line } from "react-icons/ri";
import {
  CustomSelect,
  Item,
} from "src/components/Inputs/CustomSelect/CustomSelect";
import { FormikSwitchableContent } from "src/components/Inputs/FormikSwitchableContent";
import { WithConfig } from "src/components/Permissions/WithConfig";
import { WithFeatureFlags } from "src/components/Permissions/WithFeatureFlags";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import * as AF from "src/types/formTemplate";
import { z } from "zod";
import { isNewVerification, NEW_VERIFICATION_ID } from "../../../contents";
import { useEditFormTemplateContext } from "../../../context";
import { useLiveEditConfirmation } from "../../LiveEditConfirmation";
import { InputControlWithQuestionId } from "../InputControlWithQuestionId";
import * as QuestionType from "./QuestionType";

// Default verification state
const getDefaultVerificationState = () => ({
  id: "",
  label: "",
  is_new: false,
});

export function validateForm(
  verifications: AF.V2.FormVerification[],
  values: FormikType
): FormikErrors<FormikType> {
  if (
    values.verification.switch === "enabled" &&
    values.verification.is_new === true
  ) {
    const existingVerification = verifications.find(
      (v) => v.label === values.verification.label?.trim()
    );

    if (existingVerification) {
      return {
        verification: {
          label:
            "Name already exists. Please select an existing verification or rename the verification.",
        },
      };
    }
  }

  return {};
}

export const FormikSchema = z
  .object({
    verification: z.discriminatedUnion("switch", [
      z.object({
        switch: z.literal("disabled"),
        id: z.string().optional(),
        label: z.string().optional(),
        is_avela_ai_assisted: z.boolean().optional(),
        is_identity_ai_assisted: z.boolean().optional(),
        is_new: z.boolean().optional(),
      }),
      z.object({
        switch: z.literal("enabled"),
        id: z.string().trim().min(1, "Verification is required"),
        label: z.string().trim().min(1, "Verification name is required"),
        is_avela_ai_assisted: z.boolean(),
        is_identity_ai_assisted: z.boolean(),
        is_new: z.boolean(),
      }),
    ]),
    deletedVerificationIds: z.array(z.string()).optional(),
  })
  .and(QuestionType.FormikSchema);
export type FormikType = z.infer<typeof FormikSchema>;
export function toFormValue(
  question: AF.V2.Question,
  verifications: AF.V2.FormVerification[]
): FormikType {
  // if question has a formVerificationId, find the verification in the list of verifications
  // to populate the formik values
  const verification: AF.V2.FormVerification | undefined =
    "formVerificationId" in question
      ? verifications.find((v) => v.id === question.formVerificationId)
      : undefined;
  return {
    ...QuestionType.toFormValue(question),
    verification: {
      switch: verification ? "enabled" : "disabled",
      id: verification?.id ?? "",
      label: verification?.label ?? "",
      is_avela_ai_assisted: !!verification?.is_avela_ai_assisted,
      is_identity_ai_assisted: !!verification?.is_identity_ai_assisted,
      is_new: false,
    },
  };
}

type FormikVerificationType = FormikType["verification"];

type Props = { questionId: string };
export const Verification: React.FC<Props> = ({ questionId }) => {
  const formik = useFormikContext<FormikType>();
  const formikVerification = formik.values.verification;

  const context = useEditFormTemplateContext();
  const { state } = context;
  const deletedVerificationIds = formik.values.deletedVerificationIds;

  const verifications: AF.V2.FormVerification[] = React.useMemo(() => {
    const allVerifications = state.get("draft").verifications ?? [];
    if (!deletedVerificationIds?.length) {
      return allVerifications;
    }
    return allVerifications.filter(
      (v) => !deletedVerificationIds.includes(v.id)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.get("draft").verifications, deletedVerificationIds]);

  const isUnpublishedVerification = (verificationId: string) =>
    !context.isPublishedMode ||
    !context.publishedFormTemplate.verifications?.find(
      (v) => v.id === verificationId
    );

  const LiveEditConfirmation = useLiveEditConfirmation();

  const deleteConfirmationDialog = useConfirmationDialog({
    header: "Remove verification?",
    body: "This will remove the verification along with any associations to it from existing questions. This can't be undone or recovered.",
    confirmButton: {
      label: "Yes, remove verification",
      colorScheme: "primary",
    },
    cancelButton: { label: "Cancel" },
  });

  type VerificationItem = Item & AF.V2.FormVerification;

  const onSelectHandler = React.useCallback(
    async (item: VerificationItem | null) => {
      if (item === null) {
        await formik.setFieldValue("verification", {
          ...formikVerification,
          id: "",
          label: "",
          is_new: false,
        } satisfies FormikVerificationType);
        return;
      }

      if (item.key === NEW_VERIFICATION_ID) {
        await formik.setFieldValue("verification", {
          ...formikVerification,
          id: NEW_VERIFICATION_ID,
          label: "",
          is_new: true,
          is_avela_ai_assisted: false,
          is_identity_ai_assisted: false,
        } satisfies FormikVerificationType);
        await formik.setFieldTouched("verification", false);
        return;
      }

      if (await LiveEditConfirmation.confirmVerifications()) {
        await formik.setFieldValue("verification", {
          ...formikVerification,
          id: item.id,
          label: item.label,
          is_new: false,
          is_avela_ai_assisted: item.is_avela_ai_assisted ?? false,
          is_identity_ai_assisted: item.is_identity_ai_assisted ?? false,
        } satisfies FormikVerificationType);
        await formik.setFieldTouched("verification", true);
      }
    },
    [formik, formikVerification, LiveEditConfirmation]
  );

  const onDeleteHandler = React.useCallback(
    async (verificationId: string) => {
      const verificationToDelete = verifications.find(
        (v) => v.id === verificationId
      );
      if (!verificationToDelete) {
        console.error("Verification not found for deletion");
        return;
      }

      const confirmed = await deleteConfirmationDialog.confirm({
        body: `This will remove the verification "${verificationToDelete.label}" along with any associations to it from existing questions. This can't be undone or recovered.`,
      });

      if (confirmed) {
        // Add to deleted verifications for tracking
        await formik.setFieldValue("deletedVerificationIds", [
          ...(deletedVerificationIds ?? []),
          verificationId,
        ]);

        // If the deleted verification is currently selected, clear the selection
        if (formikVerification.id === verificationId) {
          await formik.setFieldValue("verification", {
            ...formikVerification,
            ...getDefaultVerificationState(),
          } satisfies FormikVerificationType);
          await formik.setFieldTouched("verification", false);
        }
      }
    },
    [
      verifications,
      deleteConfirmationDialog,
      formik,
      deletedVerificationIds,
      formikVerification,
    ]
  );

  const verificationOptions = React.useMemo(() => {
    const options: VerificationItem[] = verifications.map((v) => ({
      ...v,
      key: v.id,
    }));

    options.push({
      id: NEW_VERIFICATION_ID,
      key: NEW_VERIFICATION_ID,
      label: "Create new",
      is_avela_ai_assisted: false,
      is_identity_ai_assisted: false,
    });

    return options;
  }, [verifications]);

  const isAiAssistCheckboxDisabled =
    formik.values.type !== AF.V2.QuestionTypes.FileUpload;

  if (formikVerification.switch === "disabled") {
    return null;
  }

  return (
    <>
      <Flex direction="column" gap="3">
        <CustomSelect<VerificationItem>
          onBlur={async () => {
            await formik.setFieldTouched("verification.id", true);
          }}
          selectedItem={
            verificationOptions.find(
              (option) => option.id === formik.values.verification.id
            ) ?? null
          }
          onChange={onSelectHandler}
          error={
            formik.touched?.verification?.id
              ? formik.errors?.verification?.id
              : undefined
          }
          label="Select verification"
          helperText="Select a verification or create a new one"
          items={verificationOptions}
          renderItem={(item) => (
            <Flex justify="space-between" align="center" w="100%" px={3}>
              <Text
                flex="1"
                cursor="pointer"
                onClick={() => onSelectHandler(item)}
              >
                {item.label}
              </Text>

              {!isNewVerification(item) &&
                isUnpublishedVerification(item.id) && (
                  <Flex
                    as="span"
                    role="button"
                    aria-label={`Delete ${item.label}`}
                    onClick={(e) => {
                      onDeleteHandler(item.key);
                    }}
                    _hover={{ color: "blue.500" }}
                    color="gray.500"
                    cursor="pointer"
                    ml={2}
                  >
                    <RiDeleteBin6Line />
                  </Flex>
                )}
            </Flex>
          )}
          renderSelectedItem={(item) => (
            <Text>{item?.label ?? "Select an option"}</Text>
          )}
          renderEmptyItems={() => <Text>Create a new verification</Text>}
        />
        {isNewVerification(formikVerification) && (
          <InputControlWithQuestionId
            questionId={questionId}
            name="verification.label"
            label="New verification name"
          />
        )}

        <WithFeatureFlags featureFlags={["ai-verifications"]}>
          <WithConfig
            configType="AvelaAI"
            defaultValue={OrgConfig.AvelaAIConfigSchema.default}
          >
            <FormControl>
              <Checkbox
                name="verification.is_avela_ai_assisted"
                isChecked={formikVerification.is_avela_ai_assisted}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  void formik.setFieldValue(
                    "verification.is_avela_ai_assisted",
                    event.target.checked
                  );
                }}
                isDisabled={isAiAssistCheckboxDisabled}
              >
                <Text fontSize="md">Assist with Avela Intelligence</Text>
              </Checkbox>
              <FormHelperText ml="6">
                When checked, Avela Intelligence will scan documents uploaded as
                part of this verification and provide useful information from
                it.
              </FormHelperText>
            </FormControl>
          </WithConfig>
          <WithConfig
            configType="IdentityAI"
            defaultValue={OrgConfig.IdentityAIConfigSchema.default}
          >
            <FormControl>
              <Checkbox
                name="verification.is_identity_ai_assisted"
                isChecked={formikVerification.is_identity_ai_assisted}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  void formik.setFieldValue(
                    "verification.is_identity_ai_assisted",
                    event.target.checked
                  );
                }}
                isDisabled={isAiAssistCheckboxDisabled}
              >
                <Text fontSize="md">Assist with Identity AI</Text>
              </Checkbox>
              <FormHelperText ml="6">
                When checked, Avela will scan uploaded IDs as part of this
                verification and automatically match fields to the corresponding
                ID fields.
              </FormHelperText>
            </FormControl>
          </WithConfig>
        </WithFeatureFlags>
      </Flex>
      {deleteConfirmationDialog.confirmationDialog}
    </>
  );
};

type VerificationSwitchProps = {
  questionId: uuid;
};

export const VerificationSwitch: React.FC<VerificationSwitchProps> = ({
  questionId,
}) => {
  const id = `verificationSwitch-${questionId}`;
  return (
    <FormikSwitchableContent<"enabled" | "disabled">
      id={id}
      label="Has verification"
      name="verification.switch"
      checked={["enabled", ""]}
      unchecked={["disabled", ""]}
      showContent={(value) => value === "enabled"}
      description=""
    >
      <Verification questionId={questionId} />
    </FormikSwitchableContent>
  );
};
